import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { getApiUrl } from '../config';
import { logger } from '../utils/logger';

const DEFAULT_CONTEXT = {
  tickers: {},
  loading: true,
  error: null,
  refresh: () => {}
};

export const CryptoMarketContext = createContext(DEFAULT_CONTEXT);

const transformTickerData = (rawTickers = {}) => {
  return Object.entries(rawTickers).reduce((acc, [symbol, payload]) => {
    if (!symbol) {
      return acc;
    }

    const safeGet = (value, fallback = 0) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : fallback;
    };

    const lastPrice = safeGet(payload?.price ?? payload?.lastPrice ?? payload?.midPrice);
    const change24h = safeGet(
      payload?.change24h ?? payload?.priceChange24h ?? payload?.change_24h
    );
    const changePercent24h = safeGet(
      payload?.changePercent24h ?? payload?.priceChangePercent24h ?? payload?.changePercent ?? 0
    );
    const volume24h = safeGet(payload?.volume24h ?? payload?.volume ?? payload?.quoteVolume);
    const marketCap = safeGet(payload?.marketCap ?? payload?.market_cap ?? 0);

    acc[symbol] = {
      price: lastPrice,
      change24h,
      changePercent24h,
      volume24h,
      marketCap
    };

    return acc;
  }, {});
};

export const CryptoMarketProvider = ({ children, pollInterval = 30000 }) => {
  const [tickers, setTickers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const { isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const handleAuthError = useCallback((authError) => {
    const errorCode = authError?.error || authError?.code || authError?.error_description;
    const needsReauth = ['login_required', 'consent_required', 'interaction_required'].includes(errorCode);

    if (needsReauth) {
      loginWithRedirect({
        appState: { returnTo: window.location.pathname }
      });
      return true;
    }

    return false;
  }, [loginWithRedirect]);

  const fetchMarketData = useCallback(async () => {
    if (!isAuthenticated) {
      setTickers({});
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Try fetching from CryptoCompare API first
      const symbols = ['BTC', 'ETH', 'SOL', 'USDT', 'USDC', 'BNB', 'XRP', 'ADA', 'AVAX'];
      const response = await axios.get(
        `https://min-api.cryptocompare.com/data/pricemultifull?fsyms=${symbols.join(',')}&tsyms=USD`,
        {
          headers: {
            authorization: `Apikey ${process.env.REACT_APP_CRYPTOCOMPARE_API_KEY || ''}`
          }
        }
      );

      if (response.data && response.data.RAW) {
        const formattedData = {};
        Object.keys(response.data.RAW).forEach(symbol => {
          const data = response.data.RAW[symbol].USD;
          formattedData[`${symbol}/USD`] = {
            price: data.PRICE,
            change24h: data.CHANGE24HOUR,
            changePercent24h: data.CHANGEPCT24HOUR,
            volume24h: data.VOLUME24HOUR,
            marketCap: data.MKTCAP
          };
        });
        setTickers(formattedData);
      }
    } catch (err) {
      logger.warn('Failed to fetch from CryptoCompare, using mock data:', err.message);

      // Fallback to mock data for demo
      const mockTickers = {
        'BTC/USD': { price: 107000, changePercent24h: 2.5, change24h: 2500, volume24h: 25000000000, marketCap: 2100000000000 },
        'ETH/USD': { price: 3800, changePercent24h: 1.8, change24h: 68, volume24h: 15000000000, marketCap: 450000000000 },
        'SOL/USD': { price: 185, changePercent24h: 3.2, change24h: 5.92, volume24h: 3000000000, marketCap: 85000000000 },
        'USDT/USD': { price: 1.0, changePercent24h: 0.01, change24h: 0.0001, volume24h: 50000000000, marketCap: 95000000000 },
        'USDC/USD': { price: 1.0, changePercent24h: -0.02, change24h: -0.0002, volume24h: 6000000000, marketCap: 25000000000 },
        'BNB/USD': { price: 620, changePercent24h: -1.2, change24h: -7.44, volume24h: 1500000000, marketCap: 95000000000 },
        'XRP/USD': { price: 2.15, changePercent24h: 4.5, change24h: 0.097, volume24h: 2500000000, marketCap: 120000000000 },
        'ADA/USD': { price: 1.05, changePercent24h: -0.5, change24h: -0.0053, volume24h: 800000000, marketCap: 37000000000 },
        'AVAX/USD': { price: 38.5, changePercent24h: 2.1, change24h: 0.81, volume24h: 600000000, marketCap: 15000000000 }
      };
      setTickers(mockTickers);
      setError(null); // Don't show error since we have fallback data
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    fetchMarketData();

    if (pollInterval > 0) {
      pollRef.current = setInterval(fetchMarketData, pollInterval);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, [fetchMarketData, pollInterval, isLoading]);

  const value = useMemo(
    () => ({
      tickers,
      loading,
      error,
      refresh: fetchMarketData
    }),
    [tickers, loading, error, fetchMarketData]
  );

  return (
    <CryptoMarketContext.Provider value={value}>
      {children}
    </CryptoMarketContext.Provider>
  );
};

export const useCryptoMarketData = () => {
  const context = useContext(CryptoMarketContext);
  if (!context) {
    throw new Error('useCryptoMarketData must be used within a CryptoMarketProvider');
  }
  return context;
};
